import { Suspense } from 'react';
import { Helmet } from 'react-helmet-async';
import { HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { env } from '@/config/env';
import { useAuthStore } from '@/store/useAuthStore';
import { Article, Main } from '.';
import AuthHeader from '../auth/header';
import AuthNavbar from '../auth/navbar';
import { Background } from '../background';
import ReactTopLoader from '../react-top-loader/ReactTopLoader';
import AuthNavbarSkeleton from '../skeletons/AuthNavbarSkeleton';

/**
 * Root layout component that wraps the application content and applies global styles or metadata.
 *
 * @param {Object} props - Component props.
 * @param {string} [props.className] - Optional additional CSS classes for the root layout container.
 * @param {string} [props.articleClassName] - Optional additional CSS classes for the article wrapper.
 * @param {React.ReactNode} props.children - The child components or elements to be rendered inside the layout.
 * @param {Object} props.metadata - Optional metadata for the layout, such as title, description, or other meta tags.
 * @param {string} props.metadata.title - The title for the document (e.g., for setting document.title).
 * @param {string} [props.metadata.description] - The description for the document (e.g., for meta description tags).
 * @returns {JSX.Element} The root layout component.
 */
export default function RootLayout({
  className,
  articleClassName,
  children,
  metadata,
}) {
  const { t } = useTranslation(['tables']);

  const user = useAuthStore((state) => state.user);
  const authClass = user ? 'app__layout--auth' : 'app__layout--default';

  return (
    <HelmetProvider>
      <Helmet>
        <title>
          {metadata.title ? `${metadata.title} - HLB` : 'Home - HLB'}
        </title>
        <link rel="canonical" href={env.siteURL} />
        <meta name="robots" content="noindex, indexifembedded" />
        <meta name="robots" content="nofollow" />
        <meta name="robots" content="noarchive" />
        <meta name="robots" content="noimageindex" />
        <meta name="robots" content="notranslate" />
        <meta name="robots" content="nositelinkssearchbox" />
        <meta name="description" content="Home - HLB" />
        <meta name="keywords" content="Home - HLB" />
        <meta name="author" content="Danijel Dzakula" />
      </Helmet>

      <div className={clsx('app__layout', authClass)}>
        <ReactTopLoader />

        {user && (
          <Suspense fallback={<AuthNavbarSkeleton />}>
            <AuthNavbar />
          </Suspense>
        )}

        <Main className={clsx('auth__content', className)}>
          {user && (
            <Suspense fallback={<div>{t('loading', { ns: 'tables' })}</div>}>
              <AuthHeader />
            </Suspense>
          )}

          <Article className={clsx(articleClassName)}>
            <Background
              source="/assets/images/bg-pattern.svg"
              className="z-[0]"
            />

            {children}
          </Article>
        </Main>
      </div>
    </HelmetProvider>
  );
}
